import React, { memo, useCallback } from 'react';
import cx from 'clsx';

import { getDotValue } from '@/utils';

import { TableProps, ColumnProps } from './types';
import { ExpandButton, ArrowExpandButton } from './ExpandButton';

export const Cell = memo(
  <T extends Record<string, any>>({
    column,
    row,
    rowIndex,
    skeleton,
  }: {
    column: ColumnProps<T>;
    row: T;
    rowIndex: number;
    skeleton?: boolean;
  }) => {
    return (
      <div
        className={cx(
          'db-table-cell',
          column.sticky && 'is-sticky',
          column.align && `is-${column.align}`,
        )}
        style={{ width: column.width }}>
        {skeleton
          ? column.skeleton
          : column.render
            ? column.render({
                cellData: column.key ? getDotValue(row, column.key) : null,
                row,
                rowIndex,
              })
            : column.key
              ? getDotValue(row, column.key)
              : null}
      </div>
    );
  },
);

export const Cells = memo(
  <T extends Record<string, any>>({
    columns,
    row,
    subRowColumns,
    rowIndex,
    expandedRowRender,
    onExpandChange,
    expanded,
    expandedIconPosition,
    skeleton,
  }: {
    row: T;
    rowIndex: number;
    expanded?: boolean;
    onExpandChange?(): void;
  } & Pick<
    TableProps<T>,
    'columns' | 'subRowColumns' | 'expandedRowRender' | 'expandedIconPosition' | 'skeleton'
  >) => {
    const handlePrevent = useCallback((e: React.MouseEvent<HTMLDivElement>) => {
      e.stopPropagation();
    }, []);

    return (
      <>
        <div className="db-table-row">
          {expandedRowRender && expandedIconPosition !== 'right' ? (
            <div className="db-table-expandBtnWrap">
              <ExpandButton onClick={onExpandChange} collapsed={expanded} />
            </div>
          ) : null}
          {columns.map((column, i) => {
            return (
              <Cell
                key={(column.key as string) || i}
                column={column}
                row={row}
                rowIndex={rowIndex}
                skeleton={skeleton}
              />
            );
          })}
          {expandedRowRender && expandedIconPosition === 'right' ? (
            <div className="db-table-expandBtnWrap">
              <ArrowExpandButton onClick={onExpandChange} collapsed={expanded} />
            </div>
          ) : null}
        </div>
        {expanded ? (
          <div className="db-table-expandedRow" onClick={handlePrevent}>
            {expandedRowRender!({ row, rowIndex })}
          </div>
        ) : null}
        {subRowColumns &&
          subRowColumns.map((subColumns, i) => (
            <div className="db-table-subRow" key={i}>
              {subColumns.map((column, i) => {
                return (
                  <Cell
                    key={(column.key as string) || i}
                    column={column}
                    row={row}
                    rowIndex={rowIndex}
                  />
                );
              })}
            </div>
          ))}
      </>
    );
  },
);
